import { css } from "@emotion/css";
import { Badge, Callout, InlineMessage } from "@octopusdeploy/design-system-components";
import { BanIcon, CheckCircleIcon, UnavailableIcon, XmarkCircleIcon } from "@octopusdeploy/design-system-icons";
import { space } from "@octopusdeploy/design-system-tokens";
import type { KubernetesTentacleEndpointResource, MachineResource } from "@octopusdeploy/octopus-server-client";
import { EndpointsHelper, isDeploymentTarget, KubernetesMonitorStatus, UpgradeStatus } from "@octopusdeploy/octopus-server-client";
import React from "react";
import { useAnalyticKubernetesAgentSetupDispatcher, useCorrelationId } from "~/analytics/Analytics";
import { OctopusPermissionsControllerInstallDialog } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/OctopusPermissionsController/OctopusPermissionsControllerInstallDialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { FormSection } from "~/components/form";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import SectionHeading from "~/primitiveComponents/dataDisplay/Section/SectionHeading";
interface BundledComponentsProps {
    machine: MachineResource;
    isRunningInHACluster: boolean;
}
const styles = {
    haWarning: css({
        marginTop: space[8],
    }),
    versionContainer: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: space[8],
    }),
};
function getKubernetesMonitorStatusBadge(status: KubernetesMonitorStatus) {
    switch (status) {
        case KubernetesMonitorStatus.Online:
            return <Badge size="medium" variant="success" label="Online" icon={<CheckCircleIcon size={20}/>}/>;
        case KubernetesMonitorStatus.Offline:
            return <Badge size="medium" variant="danger" label="Offline" icon={<XmarkCircleIcon size={20}/>}/>;
        case KubernetesMonitorStatus.NotRegistered:
            return <Badge size="medium" variant="warning" label="Not Registered" icon={<BanIcon size={20}/>}/>;
    }
}
function getPermissionsControllerStatusBadge(version: string | null | undefined) {
    if (version) {
        return <Badge size="medium" variant="success" label="Online" icon={<CheckCircleIcon size={20}/>}/>;
    }
    else {
        return <Badge size="medium" variant="neutral" label="Unavailable" icon={<UnavailableIcon size={20}/>}/>;
    }
}
export function BundledComponents({ machine, isRunningInHACluster }: BundledComponentsProps) {
    const octopusPermissionsControllerFeatureToggleEnabled = useOctopusFeatureToggle("octopus-permissions-controller", false);
    const isGrpcHaEnabled = useOctopusFeatureToggle("grpc-high-availability", false);
    const { isOpen, openDialog, closeDialog } = useDialogTrigger();
    const correlationId = useCorrelationId();
    const analyticsDispatcher = useAnalyticKubernetesAgentSetupDispatcher(correlationId, "Machine Settings");
    if (!EndpointsHelper.isKubernetesTentacle(machine.Endpoint)) {
        return null;
    }
    const endpoint: KubernetesTentacleEndpointResource = machine.Endpoint;
    const kubernetesAgentDetails = endpoint.KubernetesAgentDetails;
    if (!kubernetesAgentDetails) {
        return null;
    }
    const showKubernetesMonitor = kubernetesAgentDetails.KubernetesMonitorEnabled;
    const showPermissionsController = octopusPermissionsControllerFeatureToggleEnabled && isDeploymentTarget(machine);
    const shouldShowSection = showKubernetesMonitor || showPermissionsController;
    if (!shouldShowSection) {
        return null;
    }
    const disabledBecauseHaIsNotSupported = isRunningInHACluster && !isGrpcHaEnabled;
    const monitorStatusBadge = getKubernetesMonitorStatusBadge(kubernetesAgentDetails.KubernetesMonitorStatus);
    const permissionsControllerStatusBadge = getPermissionsControllerStatusBadge(kubernetesAgentDetails.OctopusPermissionsControllerVersion);
    const permissionsControllerVersion = kubernetesAgentDetails.OctopusPermissionsControllerVersion;
    const action = {
        label: "Upgrade now",
        onClick: () => openDialog(),
    };
    const permissionsControllerVersionMessage = kubernetesAgentDetails.PermissionsControllerUpgradeStatus === UpgradeStatus.UpgradeAvailable ? <InlineMessage type={"information"} title={"New version available"} description={""} action={action}/> : null;
    const permissionsControllerVersionDisplay = (<span className={styles.versionContainer}>
            {permissionsControllerVersion}
            {permissionsControllerVersionMessage}
        </span>);
    return (<>
            <SectionHeading title="Bundled Components"/>
            <section>
                {showKubernetesMonitor && (<>
                        <FormSection title="Kubernetes Monitor Status" help={monitorStatusBadge} includeBorder={true}/>
                        {disabledBecauseHaIsNotSupported && (<div className={styles.haWarning}>
                                <Callout type="warning" title="Kubernetes Monitor is not supported in HA clusters"/>
                            </div>)}
                    </>)}
                {showPermissionsController && (<>
                        <FormSection title="Permissions Controller Status" help={permissionsControllerStatusBadge} includeBorder={true}/>
                        {permissionsControllerVersion && <FormSection title="Permissions Controller Version" help={permissionsControllerVersionDisplay} includeBorder={true}/>}
                        <OctopusPermissionsControllerInstallDialog machine={machine} open={isOpen} closeDialog={closeDialog} isUpgrading={true} dispatchAction={analyticsDispatcher}/>
                    </>)}
            </section>
        </>);
}
